<template>
	<div>
		<div v-if="smallImageUrls.length" id="bundle-viewer" :style="style">
			<v-stage :config="stageConfig"
					 ref="stage"
					 @mousemove="stageMouseMoveHandler"
					 @scroll.native="zoom"
			>
				<v-layer>
					<v-group ref="bundleGroup"
							 :config="groupConfig"
							 :dragBoundFunc="dragBoundsHandler"
							 @dragstart="dragStartHandler"
							 @dragend="dragEndHandler"
					>
						<v-image v-for="(imageConf, key) in images"
								 :key="key"
								 v-if="imageConf.image && !loading"
								 :dragBoundFunc="dragBoundsHandler"
								 :config="imageConf"
								 @mouseover="setHoverIndex(key)"
								 @mouseleave="setHoverIndex(null)"
								 @click="$emit('clicked-bundle', bundles[key])"
								 @dblclick="$emit('dblclicked-bundle', bundles[key])"
						/>
					</v-group>

					<v-shape v-if="scala" ref="scala" :config="scalaConfig"/>
				</v-layer>
			</v-stage>

			<progress v-if="loadingImages" id="image-load-progress" :value="largeBundleImages.length" :max="smallBundleImages.length">Lade Bilder...</progress>

			<q-btn v-if="supportsFullscreen" class="canvas-overlay-button" id="fullscreen-toggle" @click="toggleFullscreen" icon="fullscreen" color="primary" dense/>

			<q-btn v-if="zoomable" class="canvas-overlay-button" id="scale-dec" @click="decreaseScale" icon="remove" color="primary" dense/>
			<q-btn v-if="zoomable" class="canvas-overlay-button" id="scale-inc" @click="increaseScale" icon="add" color="primary" dense/>

			<floating-info-box
					v-if="bundles"
					:open="bundles[hoverIndex] !== undefined"
					:conf="tooltipConf"
					ref="tooltip"
			>
				<BundleInfo :bundle="bundles[hoverIndex]"/>
			</floating-info-box>
		</div>
	</div>
</template>

<script>
	import LogPropertiesResolver from "@/mixins/LogPropertiesResolver";
	import imagetypes from "@/common/imagetypes";
	import BundleInfo from "@/components/detail/BundleInfo";
	import FloatingInfoBox from "@/components/common/FloatingInfoBox";
	import {maxScale, scalaSettings} from "@/common/config";
	import {FETCH_BUNDLES_FOR_LOG, FETCH_BUNDLES_FOR_VIRTUAL_LOG} from "@/store/actions.type";
	import {ADD_BUNDLE_TO_VIRTUAL_LOG, REMOVE_BUNDLE_FROM_VIRTUAL_LOG, SET_BUNDLES} from "@/store/mutations.type";

	export default {
		name: "BundleViewer",
		components: {FloatingInfoBox, BundleInfo},
		props: {
			log: Object,
			propBundles: Array,
			height: {
				type: Number,
				default: 800
			},
			zoomable: {
				type: Boolean,
				default: false
			},
			fullscreen: {
				type: Boolean,
				default: false
			},
			scala: {
				type: Boolean,
				default: false
			},
			sortable: {
				type: Boolean,
				default: false
			},
			isVirtual: {
				type: Boolean,
				default: false
			}
		},
		mixins: [LogPropertiesResolver],
		data() {
			return {
				loading: false,
				bundles: [],
				stageConfig: {},
				images: [],
				smallBundleImages: [],
				largeBundleImages: [],
				loadingImages: false,
				hoverIndex: null,
				scalaConfig: {
					sceneFunc: this.renderScala,
				},
				groupConfig: {
					draggable: !this.sortable,
					scaleX: 1,
					scaleY: 1,
					x: 0,
					y: 0,
				},
				minScale: 1,
				highResolutionScale: 2,
				tooltipConf: {left: 0, top: 0, width: 160},
				boundBuffer: {x: 500, y: 42},
				draggingElement: null
			}
		},
		async created() {
			await this.setBundles();
		},
		mounted() {
			this.stageConfig = {
				width: this.$el.offsetWidth,
				height: this.height
			};

			document.addEventListener("fullscreenchange", this.fullscreenChange);
		},
		beforeDestroy() {
			document.removeEventListener("fullscreenchange", this.fullscreenChange);
		},
		computed: {
			smallImageUrls() {
				return this.getImageUrls(imagetypes.SMALL_PACKET).filter(image => image !== undefined);
			},
			largeImageUrls() {
				return this.getImageUrls(imagetypes.PACKET_IMAGE).filter(image => image !== undefined);
			},
			supportsFullscreen() {
				return this.fullscreen && typeof (document.body.requestFullscreen || document.body.webkitRequestFullscreen || document.body.mozRequestFullScreen || document.body.msRequestFullscreen) === "function";
			},
			style() {
				if (this.log) {
					return {
						background: '#' + this.backColor
					}
				}

				return {};
			}
		},
		watch: {
			log() {
				this.setBundles();
			},
			bundles() {
				// bundles are loaded
				this.initBundleViewer();
			},
			propBundles() {
				this.bundles = this.propBundles;
			}
		},
		methods: {
			async setBundles() {
				if (this.bundles.length) return;

				this.bundles = [];

				if (this.propBundles) {
					this.bundles = this.propBundles;
				} else if (this.log && !this.isVirtual) {
					this.bundles = await this.$store.dispatch(FETCH_BUNDLES_FOR_LOG, this.log);
				} else if (this.log && this.isVirtual) {
					this.bundles = await this.$store.dispatch(FETCH_BUNDLES_FOR_VIRTUAL_LOG, this.log);
				}
				this.$store.commit(SET_BUNDLES, this.bundles);
			},
			async initBundleViewer() {
				if (this.$el) {
					this.stageConfig.width = this.$el.offsetWidth;
				}

				this.loading = true;
				await this.loadsmallBundleImages();
				this.loading = false;

				if (this.smallBundleImages.length && this.getStage()) {
					this.images = this.getImageConfig(this.smallBundleImages);

					if (this.$refs.stage) {
						this.$refs.stage.$el.addEventListener('mousewheel', this.zoom);
					}

					this.minScale = this.getStage().height() / this.getHighestNaturalImageHeight(this.smallBundleImages);
					this.groupConfig.scaleX = this.groupConfig.scaleY = this.minScale;

					if (this.scala) {
						this.groupConfig.x = scalaSettings.width;
					}
				}
			},
			/**
			 * Returns the highest unscaled image height in given collection of images
			 * @param images
			 * @returns {number}
			 */
			getHighestNaturalImageHeight(images) {
				return Math.max(...images.map(image => image.height)) + scalaSettings.padding;
			},
			getImageConfig(images) {
				const maxHeight = this.getHighestNaturalImageHeight(images);
				let posX = 0;

				return images.filter(image => image !== false).map(image => {
					const imageConf = {
						image: image,
						width: image.width,
						height: image.height,
						x: posX,
						y: maxHeight - image.height,
						draggable: this.sortable
					};
					posX += image.width;

					return imageConf;
				})
			},
			getImageUrls(type) {
				if (this.bundles.length) {
					// extract only image urls out of bundle array
					return this.bundles.map(
						bundle => {
							if (bundle.lstImageURLs) {
								return bundle.lstImageURLs.filter(
									image => image.intType === type
								).map(
									image => image.strImageURL
								).pop()
							}
							return undefined;
						}
					);
				}
				return [];
			},
			async loadsmallBundleImages() {
				this.smallBundleImages = await Promise.all(this.smallImageUrls.map(this.loadImage));
			},
			async loadlargeBundleImages() {
				this.loadingImages = true;
				// load images and replace low resolution images
				this.largeImageUrls.map((url, index) => {
					this.loadImage(url).then(image => {
						this.largeBundleImages[index] = image;
						this.images[index].image = image;

						if (this.largeBundleImages.length === this.smallBundleImages.length) {
							this.loadingImages = false;

							if (this.groupConfig.scaleX >= this.highResolutionScale) {
								this.replaceImages(this.largeBundleImages);
							}
						}
					});
				});
			},
			loadImage(url) {
				return new Promise(resolve => {
					const image = new Image();
					image.onload = () => resolve(image);
					image.onerror = () => resolve(false);

					image.src = url;
				});
			},
			replaceImages(images) {
				images.map((image, index) => {
					this.images[index].image = image;
				});
			},
			/**
			 * @returns {Konva.Node}
			 */
			getStage() {
				if (this.$refs.stage) {
					return this.$refs.stage.getNode();
				}
				return null;
			},
			/**
			 * @returns {Konva.Node}
			 */
			getBundleGroup() {
				if (this.$refs.bundleGroup) {
					return this.$refs.bundleGroup.getNode();
				}
				return null;
			},
			/**
			 * set currently hovered bundle and show corresponding info box
			 */
			setHoverIndex(key) {
				this.hoverIndex = key;

				if (key !== null) {
					const imageConf = this.images[key];

					let newPos = (imageConf.x + imageConf.width) * this.groupConfig.scaleX + this.groupConfig.x;

					if (newPos + this.tooltipConf.width >= this.$el.offsetWidth) {
						newPos = imageConf.x * this.groupConfig.scaleX + this.groupConfig.x - this.tooltipConf.width;
					}

					this.tooltipConf.left = newPos;
				}
			},
			stageMouseMoveHandler(event) {
				this.tooltipConf.top = event.currentTarget.getPointerPosition().y;
			},
			dragStartHandler(event) {
				this.draggingElement = event.target;

				this.setHoverIndex(null);

				if (event.target instanceof Konva.Image) {
					// ?
					this.images = this.getImageConfig(this.smallBundleImages);
				}
			},
			dragEndHandler(event) {
				//this.draggingElement = null;

				if (event.target instanceof Konva.Group) {
					// set new position after scaling for tooltip positioning
					this.resetGroupPosition();
				}

				if (event.target instanceof Konva.Image) {
					/*
					 * Find position to insert dragged image after drop
					 */
					const draggedBundle = this.bundles[event.target.index];
					// get first image to the right of the new position
					let newIndex = this.getBundleGroup().getChildren().findIndex((image, index) => {
						const referencePos = (index !== event.target.index ? image.getX() : this.images[event.target.index].x);

						return event.target.getX() === 0 || referencePos > event.target.getX();
					});
					/*
					 * -1 => no image found to right of new position, set to last position
					 * if newIndex is not 0 reduce by 1 to find actual position
					 */
					newIndex = Math.max(0, (newIndex === -1 ? this.getBundleGroup().getChildren().length - 1 : (newIndex > 0 ? newIndex - 1 : newIndex)));

					if (newIndex > -1 && newIndex !== event.target.index) {
						this.$store.commit(REMOVE_BUNDLE_FROM_VIRTUAL_LOG, draggedBundle);
						this.$store.commit(ADD_BUNDLE_TO_VIRTUAL_LOG, {
							bundle: draggedBundle,
							index: newIndex
						});
					}
					this.resetImagePositions();
					this.getStage().draw();
				}
			},
			/**
			 * Reset all images back to their original position
			 */
			resetImagePositions() {
				const maxHeight = this.getHighestNaturalImageHeight(this.smallBundleImages);
				let posX = 0;

				this.getBundleGroup().getChildren().forEach(bundle => {
					bundle.setX(posX);
					bundle.setY(maxHeight - bundle.height());
					posX += bundle.width();
				});
			},
			/**
			 * Updates group position after scaling
			 */
			resetGroupPosition(scaleMultiplier = 1) {
				this.getBundleGroup().getLayer().draw();

				const clientRect = this.getBundleGroup().getClientRect();

				let pos = this.dragBounds({x: clientRect.x, y: clientRect.y}, scaleMultiplier);

				// set config values for tooltip positioning
				this.groupConfig.x = pos.x;
				this.groupConfig.y = pos.y;
			},
			dragBoundsHandler(pos) {
				return this.dragBounds(pos);
			},
			/**
			 * Sets bounds for drag actions
			 * prevents user from dragging the group completely out of the viewport
			 */
			dragBounds(pos, scaleMultiplier = 1) {
				if (this.draggingElement === null) {
					return pos;
				}

				const
					clientRect = this.draggingElement.getClientRect(),
					stageWidth = this.getStage().width(),
					stageHeight = this.getStage().height();

				let newPos = {...pos};

				// upper and lower limit for coordinates
				const bounds = {
					upper: {
						x: stageWidth - clientRect.width * scaleMultiplier,
						y: stageHeight - clientRect.height * scaleMultiplier - this.boundBuffer.y * this.groupConfig.scaleY
					},
					lower: {
						x: scalaSettings.width,
						y: 0
					}
				};

				/*
				 * Restrict object to given bounds
				 * allow more free movement when the object fits fully inside the stage since the object may not be sticking to any stage edge
				 */
				if (stageWidth > clientRect.width) {
					if (pos.x > bounds.upper.x) {
						newPos.x = bounds.upper.x;
					}
					if (pos.x < bounds.lower.x) {
						newPos.x = bounds.lower.x;
					}
				} else {
					if (pos.x < bounds.upper.x) {
						newPos.x = bounds.upper.x;
					}
					if (pos.x > bounds.lower.x) {
						newPos.x = bounds.lower.x;
					}
				}

				if (stageHeight > clientRect.height) {
					if (pos.y > bounds.upper.y) {
						newPos.y = bounds.upper.y;
					}
					if (pos.y < bounds.lower.y) {
						newPos.y = bounds.lower.y;
					}
				} else {
					if (pos.y < bounds.upper.y) {
						newPos.y = bounds.upper.y;
					}
					if (pos.y > bounds.lower.y) {
						newPos.y = bounds.lower.y;
					}
				}

				return newPos;
			},
			/**
			 * Sets zoom scale to given value
			 */
			setScale(scale) {
				let prevScale = this.groupConfig.scaleX;

				// start loading high resolution images when user zooms for the first time
				if (this.largeBundleImages.length === 0 && !this.loadingImages) {
					this.loadlargeBundleImages();
				}

				if (scale <= maxScale) {
					this.groupConfig.scaleX = this.groupConfig.scaleY = scale;
				}

				if (this.groupConfig.scaleX < scale && scale === this.highResolutionScale && !this.loadingImages) {
					this.replaceImages(this.largeBundleImages);
				}
				if (this.groupConfig.scaleX > scale && scale === this.highResolutionScale - 1 && !this.loadingImages) {
					this.replaceImages(this.smallBundleImages);
				}

				// set new position after scaling for tooltip positioning
				this.resetGroupPosition(scale / prevScale);
			},
			/**
			 * Increases zoom scale by 1
			 */
			increaseScale() {
				if (this.groupConfig.scaleX < 1) {
					this.setScale(1);
				} else {
					this.setScale(this.groupConfig.scaleX + 1);
				}
			},
			/**
			 * Decreases zoom scale by 1
			 */
			decreaseScale() {
				if (this.groupConfig.scaleX <= 1) {
					this.setScale(this.minScale);
				} else {
					this.setScale(this.groupConfig.scaleX - 1);
				}
			},
			/**
			 * Zoom on scroll event towards current pointer position
			 * @param event
			 */
			zoom(event) {
				if (this.isInFullscreen()) {
					event.preventDefault();

					const
						scroll = (event.deltaY < 0 ? 1 : -1),
						stage = this.getStage(),
						group = this.getBundleGroup(),
						oldScale = this.groupConfig.scaleX,
						mousePointTo = {
							x: stage.getPointerPosition().x / oldScale - group.x() / oldScale,
							y: stage.getPointerPosition().y / oldScale - group.y() / oldScale
						};

					// get new scaling value
					let newScale = Math.max((this.groupConfig.scaleX + scroll), this.minScale);

					// zoom to scale=1 from initial zoom (minScale)
					if (this.groupConfig.scaleX < 1 && scroll > 0) {
						newScale = 1;
					}
					// zoom back to minScale when scale=1
					if (newScale < 1) {
						newScale = this.minScale;
					}

					if (newScale <= maxScale) {
						// center zoom around pointer position
						group.position({
							x: -(mousePointTo.x - stage.getPointerPosition().x / newScale) * newScale,
							y: -(mousePointTo.y - stage.getPointerPosition().y / newScale) * newScale
						});
						this.setScale(newScale);
					}
				}
			},
			/**
			 * Renders the lefthand dynamic scala
			 * @param {CanvasRenderingContext2D} ctx
			 * @param {Konva.Shape} shape
			 */
			renderScala(ctx, shape) {
				const
					stage = this.getStage(),
					group = this.getBundleGroup(),
					groupClientRect = group.getClientRect(),
					totalSteps = this.maxLength / scalaSettings.axisStep,
					distancePerStep = groupClientRect.height / totalSteps;
				let
					axisValue = 0,
					axisPosition = groupClientRect.height + groupClientRect.y;

				ctx.fillStyle = scalaSettings.scalaFillStyle;
				ctx.strokeStyle = scalaSettings.strokeStyle;
				ctx.font = scalaSettings.fontSize + 'px ' + scalaSettings.fontFamily;
				ctx.textAlign = scalaSettings.textAlign;

				ctx.fillRect(0, 0, scalaSettings.width, stage.getHeight());

				for (let i = 0; i <= totalSteps; i++) {

					if (i % 2 === 0) {
						this.renderScalaStep(ctx, axisValue, axisPosition, scalaSettings.big);
					} else {
						this.renderScalaStep(ctx, axisValue, axisPosition, scalaSettings.small);
					}

					axisValue += scalaSettings.axisStep;
					axisPosition -= distancePerStep;
				}
			},
			/**
			 *
			 * @param {CanvasRenderingContext2D} ctx
			 * @param {int} axisValue
			 * @param {number} axisPosition
			 * @param {object} stepConf
			 */
			renderScalaStep(ctx, axisValue, axisPosition, stepConf) {
				ctx.fillStyle = scalaSettings.fillStyle;

				if (stepConf.showLabel) {
					ctx.fillText(axisValue.toString(), scalaSettings.width + scalaSettings.textOffset.x, axisPosition + scalaSettings.textOffset.y);
				}

				ctx.fillRect(scalaSettings.width - stepConf.width, axisPosition - stepConf.height / 2, stepConf.width, stepConf.height);
			},
			isInFullscreen() {
				return (document.fullscreenElement || document.webkitFullscreenElement || document.mozFullScreenElement || document.msFullscreenElement) !== undefined;
			},
			fullscreenChange() {
				if (this.isInFullscreen()) {
					this.stageConfig.width = window.outerWidth;
					this.stageConfig.height = window.outerHeight;
				} else {
					this.stageConfig.width = this.$el.offsetWidth;
					this.stageConfig.height = 800;
				}
			},
			toggleFullscreen() {
				if (!this.isInFullscreen()) {
					this.enterFullscreen();
				} else {
					this.exitFullscreen();
				}
			},
			enterFullscreen() {
				if (this.$el.requestFullscreen) {
					this.$el.requestFullscreen();
				} else if (this.$el.webkitRequestFullScreen) {
					this.$el.webkitRequestFullScreen();
				} else if (this.$el.mozRequestFullScreen) {
					this.$el.mozRequestFullScreen();
				} else if (this.$el.msRequestFullscreen) {
					this.$el.msRequestFullscreen();
				}
			},
			exitFullscreen() {
				if (document.exitFullscreen) {
					document.exitFullscreen();
				} else if (document.webkitExitFullScreen) {
					document.webkitExitFullScreen();
				} else if (document.mozCancelFullScreen) {
					document.mozCancelFullScreen();
				} else if (document.msExitFullscreen) {
					document.msExitFullscreen();
				}
			}
		}
	}
</script>

<style lang="scss" scoped>
	#bundle-viewer {
		position: relative;

		#image-load-progress {
			position: absolute;
			top: 0;
			left: 0;
			width: 100%;
			height: 10px;
		}

		.canvas-overlay-button {
			position: absolute;
			opacity: 0.5;
			transition: opacity 0.5s ease;

			&:hover {
				opacity: 1;
			}
		}

		#fullscreen-toggle {
			top: 10px;
			right: 50px;
		}

		#scale-inc {
			bottom: 10px;
			right: 50px;
		}

		#scale-dec {
			bottom: 10px;
			right: 90px;
		}
	}
</style>